// src/pages/NotFoundPage.js
import React from 'react';
import { Link } from 'react-router-dom';
import { HomeIcon } from '@heroicons/react/24/solid';
import { OptimizedImage } from '../utils/imageManager';

const NotFoundPage = () => {
  return (
    <main className="min-h-screen bg-background-primary flex flex-col items-center justify-center text-center p-4 relative overflow-hidden">
      {/* Background Pattern with Floating Bubbles */}
      <div className="absolute inset-0 opacity-10">
        {/* Large bubbles */}
        <div
          className="absolute top-1/4 left-1/4 rounded-full animate-float-up"
          style={{
            background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
            width: '120px',
            height: '120px',
            animationDelay: '0s',
            animationDuration: '8s',
          }}
        />
        <div
          className="absolute top-1/3 right-1/4 rounded-full animate-float-up"
          style={{
            background: 'linear-gradient(135deg, var(--color-accent), var(--color-primary))',
            width: '80px',
            height: '80px',
            animationDelay: '2s',
            animationDuration: '10s',
          }}
        />
        <div
          className="absolute bottom-1/4 left-1/3 rounded-full animate-float-up"
          style={{
            background: 'linear-gradient(135deg, var(--color-secondary), var(--color-accent))',
            width: '100px',
            height: '100px',
            animationDelay: '4s',
            animationDuration: '12s',
          }}
        />
        <div
          className="absolute bottom-1/3 right-1/3 rounded-full animate-float-up"
          style={{
            background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))',
            width: '60px',
            height: '60px',
            animationDelay: '6s',
            animationDuration: '9s',
          }}
        />
      </div>

      {/* Main Content */}
      <div className="relative max-w-lg w-full animate-slide-in-center">
        {/* Fox Image with enhanced glow */}
        <div className="relative mb-8">
          <div
            className="absolute inset-0 rounded-full blur-3xl opacity-40"
            style={{
              background: 'radial-gradient(circle, var(--color-primary), var(--color-secondary), transparent 70%)',
              transform: 'scale(1.2)',
            }}
          />
          <OptimizedImage
            imageKey="NOT_FOUND"
            className="relative w-48 h-auto mx-auto drop-shadow-2xl"
            style={{
              filter: 'brightness(1.1) contrast(1.1)',
            }}
            fallback={<span className="text-8xl">😢</span>}
          />
        </div>

        {/* Headlines */}
        <div className="mb-6">
          <h1 className="text-4xl sm:text-5xl font-black text-content-primary mb-3 drop-shadow-sm">Oops! Page Not Found</h1>
          <p className="text-2xl sm:text-3xl font-bold text-content-primary opacity-80 drop-shadow-sm" lang="ja">
            ページが見つかりません
          </p>
        </div>

        {/* Sub-headlines */}
        <div className="mb-10">
          <p className="text-lg text-content-secondary opacity-90 mb-2 drop-shadow-sm">
            We can&apos;t seem to find the page you&apos;re looking for.
          </p>
          <p className="text-base text-content-secondary opacity-80 drop-shadow-sm" lang="ja">
            お探しのページが見つからないようです。
          </p>
        </div>

        {/* Back to Home Button */}
        <div className="relative">
          <Link
            to="/"
            className="inline-flex items-center px-8 py-4 rounded-2xl font-bold text-white transition-all duration-300 hover:shadow-lg hover:-translate-y-1 active:scale-95"
            style={{
              backgroundColor: 'var(--color-primary)',
            }}
          >
            <HomeIcon className="h-6 w-6 mr-3 drop-shadow-sm" />
            <span className="text-lg drop-shadow-sm">Go Back Home</span>
          </Link>
        </div>
      </div>

      {/* Additional floating elements for atmosphere */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-10 left-10 w-3 h-3 rounded-full opacity-60 animate-float-up"
          style={{
            background: 'var(--color-primary)',
            animationDelay: '1s',
            animationDuration: '6s',
          }}
        />
        <div
          className="absolute top-20 right-20 w-2 h-2 rounded-full opacity-40 animate-float-up"
          style={{
            background: 'var(--color-secondary)',
            animationDelay: '3s',
            animationDuration: '8s',
          }}
        />
        <div
          className="absolute bottom-20 left-20 w-4 h-4 rounded-full opacity-50 animate-float-up"
          style={{
            background: 'var(--color-accent)',
            animationDelay: '5s',
            animationDuration: '10s',
          }}
        />
        <div
          className="absolute bottom-10 right-10 w-2 h-2 rounded-full opacity-30 animate-float-up"
          style={{
            background: 'var(--color-primary)',
            animationDelay: '7s',
            animationDuration: '12s',
          }}
        />
      </div>
    </main>
  );
};

export default NotFoundPage;
